import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Organizaciones»: the counts strip, one page of organisations, the plan catalogue the filter and the create modal
 * offer, and one page of impersonation sessions in one answer. Each schema mirrors the interface the services return
 * (`AdminTenantResponse`, `TenantStatsResponse`, `PricingPlan` cut to four keys); the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-tenants-screen.type-assertions.ts` tie them in both
 * directions. An optional limit (`usersLimit`, `transactionsLimit`, `apiCallsLimit`) is ABSENT when the plan sets none,
 * and `suspendedAt` / `suspendedReason` come only with `status: 'suspended'`: the interfaces say it, the schema does
 * not enforce it. No row carries a credential: a session has
 * no `tokenHash` and a plan has no `tenantCount`, `limits` or `pricing`.
 */

const tenantStatusValueSchema = z.enum(['active', 'suspended', 'pending_deletion'])

export const adminTenantRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  country: z.string().nullable(),
  status: tenantStatusValueSchema,
  /** Text: the plans live in the registry of the database, not in a closed enum. */
  planType: z.string(),
  usersCount: z.number(),
  usersLimit: z.number().optional(),
  storageUsed: z.number(),
  storageLimit: z.number(),
  transactionsLimit: z.number().optional(),
  apiCallsLimit: z.number().optional(),
  createdAt: z.string(),
  lastActivityAt: z.string().nullable(),
  suspendedAt: z.string().optional(),
  suspendedReason: z.string().optional(),
})

export const tenantStatsSchema = z.strictObject({
  total: z.number(),
  active: z.number(),
  suspended: z.number(),
  pendingDeletion: z.number(),
  newThisMonth: z.number(),
  newLastMonth: z.number(),
  byPlan: z.record(z.string(), z.number()),
})

export const tenantPlanOptionSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  isActive: z.boolean(),
})

export const impersonationSessionStatusSchema = z.enum(['active', 'expired', 'terminated'])

export const impersonationSessionRowSchema = z.strictObject({
  sessionId: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  superAdminId: z.string(),
  superAdminName: z.string(),
  superAdminEmail: z.string(),
  targetUserId: z.string(),
  targetUserName: z.string(),
  reason: z.string().nullable(),
  startedAt: z.string(),
  expiresAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  status: impersonationSessionStatusSchema,
})

/**
 * The plan of the filter is checked against the registry in the composition (a contract cannot know the plans of the
 * database): an unknown one is a 400 for the whole screen. `sessionsStatus` filters ONLY the `sessions` section;
 * absent means every session.
 */
export const adminTenantsQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(10),
  status: tenantStatusValueSchema.optional(),
  planType: z.string().min(1).max(100).optional(),
  search: z.string().min(1).max(255).optional(),
  sortBy: z.enum(['name', 'createdAt', 'usersCount']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  sessionsStatus: impersonationSessionStatusSchema.optional(),
  sessionsPage: z.coerce.number<number>().int().min(1).default(1),
  sessionsLimit: z.coerce.number<number>().int().min(1).max(100).default(10),
})

export const adminTenantsScreen = defineScreen({
  id: 'admin.tenants',
  path: '/api/screens/admin/tenants',
  access: 'super_admin',
  query: adminTenantsQuerySchema,
  // Sections run in series, in this order: `page` goes first because its composition refuses an unknown plan with a
  // 400, and that must happen before any other section is read.
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(adminTenantRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    counts: screenSection(tenantStatsSchema),
    plans: screenSection(z.array(tenantPlanOptionSchema)),
    sessions: screenSection(
      z.strictObject({
        items: z.array(impersonationSessionRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.tenants', 'admin.impersonation'],
  budget: { requests: 1, statements: 12, bytes: 80_000, p95Ms: 250 },
  summary:
    'Organizations: counts, one page of organizations, the plan catalogue and one page of impersonation sessions',
})
export type AdminTenantRow = z.infer<typeof adminTenantRowSchema>
export type TenantPlanOption = z.infer<typeof tenantPlanOptionSchema>
export type ImpersonationSessionRow = z.infer<typeof impersonationSessionRowSchema>
export type AdminTenantsQuery = ScreenQuery<typeof adminTenantsScreen>
export type AdminTenantsData = ScreenData<typeof adminTenantsScreen>
